/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import {useAuth} from '../core/Auth'
import api from '../../../api'
import {useIntl} from 'react-intl'
import {useSetupContext} from '../../../constructor/helpers/SetupContext'
import {signIn, submit2faCode} from '../utils/authApi'

enum Step {
    CREDENTIALS,
    TWO_FA,
}

export function Login() {
    const {context, refetch: contextRefetch} = useSetupContext()
    const haveRegistration = Boolean(context.sign_up)
    const intl = useIntl()
    const [step, setStep] = useState<Step>(Step.CREDENTIALS)
    const [twoFaMethod, setTwoFaMethod] = useState<'email' | 'totp' | null>(null)
    const [loading, setLoading] = useState(false)
    const {setCurrentUser} = useAuth()

    // Скрыть/Показать пароль или код (глазик)
    const [visibility, setVisibility] = useState({
        password: false,
        code: false,
    })
    const toggleVisibility = (field: keyof typeof visibility) => {
        setVisibility(prev => ({
            ...prev,
            [field]: !prev[field],
        }))
    }

    const getAuthErrorKey = (code: string) => {
        const serverKey = `AUTH.SERVER.${code}`
        const baseKey = `AUTH.${code}`

        return intl.messages[serverKey] ? serverKey : baseKey
    }

    const loginSchema = Yup.object().shape({
        email: Yup.string()
            .email(intl.formatMessage({id: 'AUTH.LOGIN_EMAIL_VALIDATION_FORMAT'}))
            .min(3, intl.formatMessage({id: 'AUTH.LOGIN_EMAIL_VALIDATION_MIN_LENGTH'}))
            .max(50, intl.formatMessage({id: 'AUTH.LOGIN_EMAIL_VALIDATION_MAX_LENGTH'}))
            .required(intl.formatMessage({id: 'AUTH.LOGIN_EMAIl_VALIDATION_REQUIRED'})),
        password: Yup.string()
            .min(8, intl.formatMessage({id: 'AUTH.LOGIN_PASSWORD_VALIDATION_MIN_LENGTH'}))
            .max(50, intl.formatMessage({id: 'AUTH.LOGIN_PASSWORD_VALIDATION_MAX_LENGTH'}))
            .required(intl.formatMessage({id: 'AUTH.LOGIN_PASSWORD_VALIDATION_REQUIRED'})),
    })

    const codeSchema = Yup.object().shape({
        code: Yup.string()
            .matches(/^\d{4,8}$/g, intl.formatMessage({id: 'AUTH.CODE_REQUIRED'}))
            .required(intl.formatMessage({id: 'AUTH.CODE_REQUIRED'})),
    })

    const formikStepCredentials = useFormik({
        initialValues: { email: '', password: '' },
        validationSchema: loginSchema,
        onSubmit: async (values, helpers) => {
            setLoading(true)
            helpers.setStatus(undefined)
            try {
                const response = await signIn(values.email, values.password)
                if (response.status === 'ok') {
                    const {data: user} = await api('users', 'get-current')
                    setCurrentUser(user)
                    contextRefetch()
                } else {
                    setTwoFaMethod(response.method)
                    setStep(Step.TWO_FA)
                }
            } catch (error: any) {
                helpers.setStatus(
                    error?.code
                        ? intl.formatMessage({ id: getAuthErrorKey(error.code), defaultMessage: error.message })
                        : error?.message
                )
            } finally {
                setLoading(false)
            }
        },
    })

    const formikStepTwoFa = useFormik({
        initialValues: { code: '' },
        validationSchema: codeSchema,
        onSubmit: async (values, helpers) => {
            setLoading(true)
            helpers.setStatus(undefined)
            try {
                await submit2faCode(values.code)
                const {data: user} = await api('users', 'get-current')
                setCurrentUser(user)
                contextRefetch()
            } catch (e: any) {
                helpers.setStatus(
                    e?.code
                        ? intl.formatMessage({ id: getAuthErrorKey(e.code), defaultMessage: e.message })
                        : e?.message
                )
            } finally {
                setLoading(false)
            }
        },
    })

    const isCredentials = step === Step.CREDENTIALS

    return (
        <form
            className='auth_form form w-100'
            onSubmit={
                isCredentials ? formikStepCredentials.handleSubmit : formikStepTwoFa.handleSubmit
            }
            noValidate
            id='kt_login_signin_form'
        >
            {/* Заголовок */}
            <div className='text-center mb-8'>
                <h1 className='auth_formTitle'>
                    {isCredentials
                        ? intl.formatMessage({id: 'AUTH.LOGIN_TITLE'})
                        : twoFaMethod === 'email'
                        ? 'Подтверждение по Email'
                        : 'Ввод кода из приложения'}
                </h1>
                <div className='auth_formDescription'>
                    {isCredentials
                        ? intl.formatMessage({id: 'AUTH.LOGIN_DESCRIPTION'})
                        : twoFaMethod === 'email'
                        ? 'Мы отправили код на вашу почту. Введите его ниже.'
                        : 'Откройте аутентификатор и введите 6-значный код.'}
                </div>
            </div>

            {/* Ошибки статуса для каждого шага */}
            {isCredentials && formikStepCredentials.status && (
                <div className='mt-5 alert alert-danger'>
                    <div className='alert-text font-weight-bold'>
                        {formikStepCredentials.status}
                    </div>
                </div>
            )}
            {!isCredentials && formikStepTwoFa.status && (
                <div className='mt-5 alert alert-danger'>
                    <div className='alert-text font-weight-bold'>{formikStepTwoFa.status}</div>
                </div>
            )}

            {isCredentials ? (
                <>
                    {/* Email */}
                    <div className='fv-row mb-8'>
                        <label className='auth_fieldLabel form-label'>
                            {intl.formatMessage({id: 'AUTH.EMAIL_TITLE'})}
                        </label>
                        <input
                            placeholder='Email'
                            {...formikStepCredentials.getFieldProps('email')}
                            className={clsx(
                                'auth_field form-control bg-transparent',
                                {
                                    'is-invalid':
                                        formikStepCredentials.touched.email &&
                                        formikStepCredentials.errors.email,
                                },
                                {
                                    'is-valid':
                                        formikStepCredentials.touched.email &&
                                        !formikStepCredentials.errors.email,
                                }
                            )}
                            type='email'
                            name='email'
                            autoComplete='off'
                        />
                        {formikStepCredentials.touched.email && formikStepCredentials.errors.email && (
                            <div className='fv-plugins-message-container'>
                                <div className='fv-help-block'>
                                    <span role='alert'>{formikStepCredentials.errors.email}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Password */}
                    <div className='fv-row mb-3'>
                        <div className='d-flex justify-content-between align-center'>
                            <label className='auth_fieldLabel form-label'>
                                {intl.formatMessage({id: 'AUTH.PASSWORD_TITLE'})}
                            </label>
                            <button
                                type='button'
                                className='btn btn-sm btn-icon btn-eye'
                                onClick={() => toggleVisibility('password')}
                            >
                                <i
                                    className={
                                        visibility.password ? 'bi bi-eye-slash' : 'bi bi-eye'
                                    }
                                    style={{fontSize: '1.25rem', color: 'white'}}
                                ></i>
                            </button>
                        </div>

                        <input
                            type={visibility.password ? 'text' : 'password'}
                            autoComplete='off'
                            {...formikStepCredentials.getFieldProps('password')}
                            className={clsx(
                                'auth_field form-control bg-transparent',
                                {
                                    'is-invalid':
                                        formikStepCredentials.touched.password &&
                                        formikStepCredentials.errors.password,
                                },
                                {
                                    'is-valid':
                                        formikStepCredentials.touched.password &&
                                        !formikStepCredentials.errors.password,
                                }
                            )}
                        />
                        {formikStepCredentials.touched.password &&
                            formikStepCredentials.errors.password && (
                                <div className='fv-plugins-message-container'>
                                    <div className='fv-help-block'>
                                        <span role='alert'>
                                            {formikStepCredentials.errors.password}
                                        </span>
                                    </div>
                                </div>
                            )}
                    </div>

                    {/* Ссылки */}
                    <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
                        <div />
                        <Link to='/auth/forgot-password' className='link-primary'>
                            {intl.formatMessage({id: 'AUTH.LOGIN_FORGOT_PASSWORD_LINK'})}
                        </Link>
                    </div>
                </>
            ) : (
                <>
                    {/* TWO-FA CODE */}
                    <div className='fv-row mb-6'>
                        <div className='d-flex justify-content-between align-center'>
                            <label className='auth_fieldLabel form-label'>
                                {twoFaMethod === 'email'
                                    ? 'Код из письма'
                                    : 'Код из аутентификатора'}
                            </label>
                            <button
                                type='button'
                                className='btn btn-sm btn-icon btn-eye'
                                onClick={() => toggleVisibility('code')}
                            >
                                <i
                                    className={visibility.code ? 'bi bi-eye-slash' : 'bi bi-eye'}
                                    style={{fontSize: '1.25rem', color: 'white'}}
                                ></i>
                            </button>
                        </div>
                        <input
                            type={visibility.code ? 'text' : 'password'}
                            inputMode='numeric'
                            pattern='[0-9]*'
                            maxLength={8}
                            className='auth_field form-control bg-transparent'
                            placeholder='••••••'
                            {...formikStepTwoFa.getFieldProps('code')}
                            autoFocus
                        />
                        {formikStepTwoFa.touched.code && formikStepTwoFa.errors.code && (
                            <div className='fv-plugins-message-container'>
                                <div className='fv-help-block'>
                                    <span role='alert'>{formikStepTwoFa.errors.code}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Кнопки */}
            <div className='d-flex flex-column mb-5'>
                <button
                    type='submit'
                    id='kt_sign_in_submit'
                    className='auth_button btn btn-primary mb-4'
                    disabled={
                        loading ||
                        (isCredentials
                            ? formikStepCredentials.isSubmitting || !formikStepCredentials.isValid
                            : formikStepTwoFa.isSubmitting || !formikStepTwoFa.isValid)
                    }
                >
                    {!loading ? (
                        <span className='indicator-label'>
                            {isCredentials
                                ? intl.formatMessage({id: 'AUTH.LOGIN_SUBMIT_BUTTON'})
                                : 'Подтвердить код'}
                        </span>
                    ) : (
                        <span className='indicator-progress' style={{display: 'block'}}>
                            {intl.formatMessage({id: 'AUTH.AWAIT_STATUS'})}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                    )}
                </button>

                {step === Step.TWO_FA && (
                    <button
                        type='button'
                        className='btn btn-light'
                        onClick={() => {
                            setStep(Step.CREDENTIALS)
                            formikStepTwoFa.resetForm()
                        }}
                        disabled={loading}
                    >
                        Назад
                    </button>
                )}
            </div>

            {/* Ссылка на регистрацию показываем только на шаге логина */}
            {haveRegistration && isCredentials ? (
                <div className='text-center fw-semibold fs-6'>
                    {intl.formatMessage({id: 'AUTH.LOGIN_REGISTRATION'})}{' '}
                    <Link to='/auth/reg' className='link-primary'>
                        {intl.formatMessage({id: 'AUTH.LOGIN_REGISTRATION_LINK'})}
                    </Link>
                </div>
            ) : null}
        </form>
    )
}
